import { useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initOtpWidget, otpErr } from "@/lib/msg91";

export default function Auth() {
  const { login, register, loginMobile, signupSendOtp, signupVerify } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/";
  const [mode, setMode] = useState(sp.get("mode") === "register" ? "register" : "login");
  const [channel, setChannel] = useState("email"); // email | mobile
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [signupOtp, setSignupOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const widgetReady = useRef(false);
  const submittedRef = useRef(false);
  const ERP_ROLES = ["super_admin", "owner", "store_manager", "staff", "salesman", "technician"];
  const roleRedirect = (role) => ERP_ROLES.includes(role) ? "/erp" : role === "seller" ? "/seller" : next;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (mode === "login") { const u = await login(f.email, f.password); toast.success("Welcome back!"); nav(roleRedirect(u.role)); }
      else { const r = await signupSendOtp({ name: f.name, email: f.email, password: f.password, phone: f.phone }); setDevOtp(r.dev_otp || ""); setOtpStep(true); toast.success("OTP aapke email par bhej diya gaya"); }
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  const verifySignup = async (e) => {
    e.preventDefault();
    if (signupOtp.trim().length < 4) { toast.error("6-digit OTP daalein"); return; }
    setBusy(true);
    try { const u = await signupVerify(f.email, signupOtp.trim()); toast.success("Account ban gaya!"); nav(roleRedirect(u.role)); }
    catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  const resendSignupOtp = async () => {
    setBusy(true);
    try { const r = await signupSendOtp({ name: f.name, email: f.email, password: f.password, phone: f.phone }); setDevOtp(r.dev_otp || ""); toast.success("Naya OTP bhej diya"); }
    catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  const ensureWidget = async () => {
    if (widgetReady.current) return;
    await initOtpWidget({
      onToken: async (token) => {
        if (submittedRef.current) return;
        submittedRef.current = true;
        try {
          const u = await loginMobile(token);
          toast.success("Login ho gaya!");
          nav(roleRedirect(u.role));
        } catch (er) { submittedRef.current = false; setBusy(false); toast.error(apiErr(er)); }
      },
      onFailure: () => setBusy(false),
    });
    widgetReady.current = true;
  };

  const sendOtp = async () => {
    const digits = mobile.replace(/\D/g, "");
    if (digits.length !== 10) { toast.error("10-digit mobile number daalein"); return; }
    setBusy(true); submittedRef.current = false;
    try {
      await ensureWidget();
      window.sendOtp(`91${digits}`,
        () => { setOtpSent(true); setBusy(false); toast.success("OTP bhej diya gaya"); },
        (e) => { setBusy(false); console.error("MSG91 sendOtp fail", e); toast.error(`OTP nahi gaya: ${otpErr(e)}`); });
    } catch { setBusy(false); toast.error("OTP service load nahi hui"); }
  };

  const verifyOtp = (e) => {
    e.preventDefault();
    if (otp.length < 4) { toast.error("OTP daalein"); return; }
    setBusy(true); submittedRef.current = false;
    try {
      window.verifyOtp(otp, undefined,
        (er) => { setBusy(false); console.error("MSG91 verifyOtp fail", er); toast.error(`OTP verify fail: ${otpErr(er)}`); });
    } catch { setBusy(false); toast.error("Verify nahi ho paya"); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "linear-gradient(135deg,#0a2f6e 0%,#0b3d91 55%,#0a2f6e 100%)" }}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 animate-fade-up">
        <Link to="/" className="flex flex-col items-center gap-2 mb-5" data-testid="auth-logo">
          <img src="/rsmart-pos-logo.png" alt="Rs. Mart POS" className="w-20 h-20 object-contain drop-shadow" />
          <span className="font-extrabold text-xl tracking-tight">Rani Super Market</span>
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full">★ Powered by Rs. Mart POS</span>
        </Link>
        <h1 className="text-2xl font-bold text-center">{channel === "mobile" ? "Mobile se login / signup" : mode === "login" ? "Welcome back" : "Create account"}</h1>
        <p className="text-center text-slate-500 text-sm mt-1">{channel === "mobile" ? "OTP se — bina password ke" : mode === "login" ? "Login to continue shopping" : "Sign up to start ordering"}</p>

        <div className="grid grid-cols-2 gap-2 mt-5 p-1 bg-slate-100 rounded-xl" data-testid="auth-channel-toggle">
          <button type="button" data-testid="channel-email-btn" onClick={() => setChannel("email")}
            className={`py-2 rounded-lg text-sm font-bold transition-colors ${channel === "email" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Email</button>
          <button type="button" data-testid="channel-mobile-btn" onClick={() => setChannel("mobile")}
            className={`py-2 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-1.5 ${channel === "mobile" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
            <Smartphone size={14} /> Mobile OTP <span className="text-[9px] font-black bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full leading-none">SOON</span>
          </button>
        </div>

        {channel === "mobile" ? (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="mobile-coming-soon">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 text-amber-600 flex items-center justify-center"><Smartphone size={22} /></div>
            <h3 className="font-extrabold text-slate-900 mt-3">Mobile OTP — Coming Soon</h3>
            <p className="text-sm text-slate-600 mt-1.5">Mobile se OTP login jald aa raha hai.<br />Abhi ke liye <b>Email + Password</b> ya <b>Google</b> se login karein.</p>
            <button type="button" data-testid="mobile-coming-soon-back" onClick={() => setChannel("email")}
              className="mt-4 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm transition-colors">Email se login karein</button>
          </div>
        ) : otpStep ? (
          <form onSubmit={verifySignup} className="mt-5 space-y-3" data-testid="signup-otp-panel">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center text-sm text-slate-600"><b>{f.email}</b> par 6-digit code bheja gaya hai. Neeche daalein.</div>
            {devOtp && <div data-testid="signup-dev-otp" className="text-center text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg py-2">Dev/Test OTP: <b className="tracking-widest">{devOtp}</b></div>}
            <input data-testid="signup-otp-input" inputMode="numeric" maxLength={6} autoFocus placeholder="Enter OTP" value={signupOtp} onChange={(e) => setSignupOtp(e.target.value.replace(/\D/g, ""))} className="w-full text-center tracking-[0.5em] text-lg border border-slate-200 rounded-xl px-4 py-3" />
            <button data-testid="signup-otp-verify" disabled={busy} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl font-bold transition-colors">{busy ? "Please wait…" : "Verify & Create Account"}</button>
            <div className="flex justify-between text-xs">
              <button type="button" data-testid="signup-otp-back" onClick={() => { setOtpStep(false); setSignupOtp(""); }} className="font-semibold text-slate-500">← Edit details</button>
              <button type="button" data-testid="signup-otp-resend" onClick={resendSignupOtp} disabled={busy} className="font-semibold text-emerald-600">Resend OTP</button>
            </div>
          </form>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            {mode === "register" && <input data-testid="auth-name" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" />}
            <input data-testid="auth-email" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" />
            {mode === "register" && <input data-testid="auth-phone" placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" />}
            <input data-testid="auth-password" type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" />
            {mode === "login" && (
              <div className="flex justify-between -mt-1">
                <Link to="/forgot-email" data-testid="forgot-email-link" className="text-xs font-semibold text-emerald-600">Forgot email ID?</Link>
                <Link to="/forgot-password" data-testid="forgot-link" className="text-xs font-semibold text-emerald-600">Forgot password?</Link>
              </div>
            )}
            <button data-testid="auth-submit" disabled={busy} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl font-bold transition-colors">{busy ? "Please wait…" : mode === "login" ? "Login" : "Sign Up"}</button>
          </form>
        )}

        {!otpStep && (<>
        <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">OR</span><div className="flex-1 h-px bg-slate-200" /></div>
        <button data-testid="google-login" onClick={google} className="w-full py-3 border border-slate-200 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors">
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5" /> Continue with Google
        </button>
        {channel === "email" && (mode === "login" ? (
          <div data-testid="new-customer-cta" className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center">
            <p className="text-sm text-slate-600 font-medium">New to Rani Super Market?</p>
            <button data-testid="auth-toggle" onClick={() => setMode("register")} className="mt-1.5 inline-block px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors">Create a free account</button>
          </div>
        ) : (
          <p className="text-center text-sm text-slate-500 mt-5">Already have an account? <button data-testid="auth-toggle" onClick={() => setMode("login")} className="text-emerald-600 font-bold">Login</button></p>
        ))}
        <p className="text-center text-xs text-slate-400 mt-2">
          Seller / Business ho? <button data-testid="auth-seller-link" onClick={() => nav("/become-seller")} className="text-emerald-600 font-bold">Seller login / register</button>
        </p>
        </>)}
      </div>
    </div>
  );
}
